/**
 * Toolbar above the waveform in the Slicer route. Houses the four
 * decisions the user needs to make to chop a sample:
 *
 *   1. BPM (primary via Bars input, fallback via Auto-detect button)
 *   2. How many equal slices to chop into (4/8/16/32/64)
 *   3. Whether snap-to-grid is on
 *   4. Save back to the server
 *
 * Playback controls ("play slice N" / "play all") are deliberately
 * elsewhere — the chop toolbar stays focused on grid + structural
 * edits so it doesn't balloon to fifteen icons.
 */

/**
 * Chop algorithm choice.
 *   - 'equal' — divide the post-trim span into N equal slices.
 *   - 'transients' — take the first N onset peaks as-is.
 *   - 'smart' — score every onset peak (transient strength +
 *     silence gap + bar alignment), pick the top-N. Surfaces as
 *     "Smart chop" in the UI.
 */
export type ChopStrategy = 'equal' | 'transients' | 'smart'

interface ChopControlsProps {
  bpm: number | undefined
  bars: number | undefined
  bpmConfidence: number | undefined
  snap: boolean
  sliceCount: number
  durationSec: number | undefined
  isDetecting: boolean
  isSaving: boolean
  /** Phase 1 — chop algorithm. 'equal' = today's behaviour;
   *  'transients' targets detected transient peaks. */
  chopStrategy: ChopStrategy
  /** Number of detected transient peaks. Disables the Transients
   *  toggle when 0 (Auto-detect needs to run first). */
  peakCount: number
  /** Detected first-transient position in seconds. When set, the
   *  "Trim to first transient" button is enabled and snaps to it. */
  autoStartSec: number | undefined
  /** Current trim value. Surfaces in the small read-out next to
   *  the trim button so the user knows what's set. */
  trimStartSec: number
  onBpmChange: (bpm: number | undefined) => void
  onBarsChange: (bars: number | undefined) => void
  onSnapChange: (snap: boolean) => void
  onSliceCountChange: (count: number) => void
  onChopStrategyChange: (strategy: ChopStrategy) => void
  onAutoDetect: () => void
  onTrimToFirstTransient: () => void
  onSave: () => void
}

const SLICE_CHOICES = [4, 8, 16, 32, 64] as const

export function ChopControls({
  bpm,
  bars,
  bpmConfidence,
  snap,
  sliceCount,
  durationSec,
  isDetecting,
  isSaving,
  chopStrategy,
  peakCount,
  autoStartSec,
  trimStartSec,
  onBpmChange,
  onBarsChange,
  onSnapChange,
  onSliceCountChange,
  onChopStrategyChange,
  onAutoDetect,
  onTrimToFirstTransient,
  onSave,
}: ChopControlsProps) {
  return (
    <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-surface-raised border border-brd-subtle">
      <label className="flex flex-col text-[10px] uppercase tracking-wider text-content-tertiary">
        BPM
        <input
          type="number"
          min={1}
          max={300}
          step={0.1}
          value={bpm ?? ''}
          placeholder="—"
          onChange={e => {
            const v = e.target.value
            onBpmChange(v === '' ? undefined : Number(v))
          }}
          className="mt-0.5 w-20 rounded-md bg-surface-sunken text-content-primary text-sm font-mono px-2 py-1 border border-brd-subtle focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </label>

      <label className="flex flex-col text-[10px] uppercase tracking-wider text-content-tertiary">
        Bars
        <input
          type="number"
          min={1}
          max={64}
          step={1}
          value={bars ?? ''}
          placeholder="—"
          onChange={e => {
            const v = e.target.value
            const n = v === '' ? undefined : Math.round(Number(v))
            onBarsChange(n)
            // When both bars and duration are known, compute BPM
            // exactly — this is the "I know this is a 4-bar loop"
            // path the plan calls primary. Always wins over the
            // autodetector (which might be off by a factor of 2).
            if (n && durationSec && durationSec > 0) {
              const exactBpm = (n * 4 * 60) / durationSec
              onBpmChange(Number(exactBpm.toFixed(2)))
            }
          }}
          className="mt-0.5 w-16 rounded-md bg-surface-sunken text-content-primary text-sm font-mono px-2 py-1 border border-brd-subtle focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </label>

      <button
        type="button"
        onClick={onAutoDetect}
        disabled={isDetecting || !durationSec}
        className="btn-secondary btn-sm disabled:opacity-50"
        title="Run onset-autocorrelation BPM detection + transient peak finding in a Web Worker"
      >
        {isDetecting ? 'Detecting…' : 'Auto-detect'}
      </button>

      {/* Half / double toggle for working tempo. Pure display — no
          audio changes. Useful when the detector lands on a tempo
          that's correct but off-by-2×, or when the user wants to
          switch feel (172 BPM DnB → 86 BPM hip-hop half-time) for
          grid / bar math without re-detecting. */}
      {bpm && bpm > 0 && (
        <div
          role="group"
          aria-label="Halve or double working BPM"
          className="inline-flex rounded-md border border-brd-subtle overflow-hidden"
        >
          <button
            type="button"
            onClick={() =>
              onBpmChange(Number(Math.max(30, bpm / 2).toFixed(2)))
            }
            disabled={!durationSec}
            title="Halve the working BPM (e.g. 172 → 86). Re-compute grid + bars from the halved tempo."
            className="px-2 py-1 text-[11px] font-mono bg-surface-sunken text-content-secondary hover:text-content-primary disabled:opacity-40"
          >
            ½×
          </button>
          <button
            type="button"
            onClick={() =>
              onBpmChange(Number(Math.min(400, bpm * 2).toFixed(2)))
            }
            disabled={!durationSec}
            title="Double the working BPM (e.g. 86 → 172)."
            className="px-2 py-1 text-[11px] font-mono bg-surface-sunken text-content-secondary hover:text-content-primary disabled:opacity-40 border-l border-brd-subtle"
          >
            2×
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={onTrimToFirstTransient}
        disabled={autoStartSec === undefined || autoStartSec <= 0.001}
        className="btn-secondary btn-sm disabled:opacity-50"
        title={
          autoStartSec === undefined
            ? 'Run Auto-detect first to find the first transient'
            : `Snap trim to first detected transient (${(autoStartSec ?? 0).toFixed(3)}s)`
        }
      >
        Trim → transient
      </button>

      {trimStartSec > 0 && (
        <span
          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 tabular-nums"
          title="Current trim point. Drag the yellow handle on the waveform to adjust."
        >
          trim {trimStartSec.toFixed(3)}s
        </span>
      )}

      {bpmConfidence !== undefined && (
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
            bpmConfidence >= 0.6
              ? 'bg-emerald-500/15 text-emerald-300'
              : bpmConfidence >= 0.4
                ? 'bg-amber-500/15 text-amber-300'
                : 'bg-rose-500/15 text-rose-300'
          }`}
          title="Higher = the autodetector is more confident. Below 40% → consider overriding via Bars."
        >
          {Math.round(bpmConfidence * 100)}% conf.
        </span>
      )}

      <div className="h-8 w-px bg-brd-subtle" aria-hidden />

      <label className="flex flex-col text-[10px] uppercase tracking-wider text-content-tertiary">
        Slices
        <select
          value={sliceCount}
          onChange={e => onSliceCountChange(Number(e.target.value))}
          className="mt-0.5 rounded-md bg-surface-sunken text-content-primary text-sm px-2 py-1 border border-brd-subtle focus:outline-none focus:ring-1 focus:ring-accent"
        >
          {SLICE_CHOICES.map(n => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      {/* Chop strategy — segmented toggle. Equal divides the
          (post-trim) duration evenly. Transients snaps each slice
          start to a detected transient peak; disabled until
          Auto-detect has produced peaks. */}
      <label className="flex flex-col text-[10px] uppercase tracking-wider text-content-tertiary">
        Strategy
        <div
          role="group"
          aria-label="Chop strategy"
          className="mt-0.5 inline-flex rounded-md border border-brd-subtle overflow-hidden"
        >
          <button
            type="button"
            onClick={() => onChopStrategyChange('equal')}
            className={`px-2 py-1 text-[11px] font-mono ${
              chopStrategy === 'equal'
                ? 'bg-accent text-[color:var(--accent-ink)]'
                : 'bg-surface-sunken text-content-secondary hover:text-content-primary'
            }`}
          >
            Equal
          </button>
          <button
            type="button"
            onClick={() => onChopStrategyChange('transients')}
            disabled={peakCount === 0}
            title={
              peakCount === 0
                ? 'Run Auto-detect first to find transients'
                : `Snap slice starts to detected transients (${peakCount} found)`
            }
            className={`px-2 py-1 text-[11px] font-mono disabled:opacity-40 ${
              chopStrategy === 'transients'
                ? 'bg-accent text-[color:var(--accent-ink)]'
                : 'bg-surface-sunken text-content-secondary hover:text-content-primary'
            }`}
          >
            Transients
          </button>
          <button
            type="button"
            onClick={() => onChopStrategyChange('smart')}
            disabled={peakCount === 0}
            title={
              peakCount === 0
                ? 'Run Auto-detect first to find transients'
                : `Smart-chop: score every transient by strength + spacing + bar alignment, pick the top ${peakCount}`
            }
            className={`px-2 py-1 text-[11px] font-mono disabled:opacity-40 ${
              chopStrategy === 'smart'
                ? 'bg-accent text-[color:var(--accent-ink)]'
                : 'bg-surface-sunken text-content-secondary hover:text-content-primary'
            }`}
          >
            Smart
          </button>
        </div>
      </label>

      <label className="flex items-center gap-2 text-xs text-content-secondary select-none mb-1">
        <input
          type="checkbox"
          checked={snap}
          onChange={e => onSnapChange(e.target.checked)}
          className="accent-accent"
        />
        Snap to grid
      </label>

      <div className="flex-1" />

      <button
        type="button"
        onClick={onSave}
        disabled={isSaving}
        className="btn-primary btn-sm disabled:opacity-50"
      >
        {isSaving ? 'Saving…' : 'Save slices'}
      </button>
    </div>
  )
}
